// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/RequesterTextField/index.tsx

import { useTextField } from '@modusign/react-signing';
import { useUIState } from '@modusign/react-signing/setup';
import { FontType, TextAlign } from '@modusign/react-signing/types';
import { useCallback } from 'react';

import { clampFieldPoint } from '@app/features/apply-react-signing/components/FieldRenderer/libs/clampFieldPoint';
import { RequesterTextField } from '@app/features/apply-react-signing/components/FieldRenderer/Setup/components/RequesterFields/RequesterTextField';
import {
  Point,
  ReactSigningFieldRendererProps,
  Size,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';
import { useOptimalFieldsContext } from '@app/features/apply-react-signing/libs/hooks/useCollectionOptimal';

function RequesterTextFieldRenderer({
  fieldId,
  viewport,
  scale,
  readOnly,
  isPreview = false,
  initialContextMenuOpened = true,
  isDataLabelEditable = true,
  showFieldNumber = false,
}: ReactSigningFieldRendererProps) {
  const {
    field,
    setPosition,
    setSize,
    setDataLabel,
    setOverrideConfigFontSize,
    setOverrideConfigFontStyle,
    setOverrideConfigAlign,
    setFontSize,
    setAlign,
    setFontStyle,
    setValue,
  } = useTextField(fieldId);
  const {
    evaluated: { fieldNumber, isDataLabelDuplicated, suggestions },
    removeField,
  } = useOptimalFieldsContext<'TEXT', 'REQUESTER'>();

  const { ui } = useUIState();

  const handlePointChange = useCallback(
    (point: Point) => {
      const clampedPoint = clampFieldPoint(point);
      setPosition(clampedPoint.x, clampedPoint.y);
    },
    [setPosition]
  );

  const handleSizeChange = useCallback(
    (size: Size) => {
      setSize(size.width, size.height);
    },
    [setSize]
  );

  const handleFontSizeChange = useCallback(
    (size: number) => {
      setFontSize(size);
      setOverrideConfigFontSize(size);
    },
    [setFontSize, setOverrideConfigFontSize]
  );

  const handleFontStyleChange = useCallback(
    (type: FontType) => {
      setFontStyle(type);
      setOverrideConfigFontStyle(type);
    },
    [setFontStyle, setOverrideConfigFontStyle]
  );

  const handleAlignChange = useCallback(
    (align: TextAlign) => {
      setAlign(align);
      setOverrideConfigAlign(align);
    },
    [setAlign, setOverrideConfigAlign]
  );

  if (field == null || field.type !== 'TEXT') {
    return null;
  }

  return (
    <RequesterTextField
      autoFocus={ui.lastInsertedField.SIGNER.TEXT?.id === fieldId}
      field={field}
      fieldNumber={isPreview || !showFieldNumber ? undefined : fieldNumber}
      initialContextMenuOpened={initialContextMenuOpened}
      isDataLabelDuplicated={isDataLabelDuplicated}
      isDataLabelEditable={isDataLabelEditable}
      isPreview={isPreview}
      readOnly={readOnly}
      scale={scale}
      suggestions={suggestions}
      viewport={viewport}
      onDataLabelChange={setDataLabel}
      onFieldRemove={removeField}
      onFontAlignChange={handleAlignChange}
      onFontSizeChange={handleFontSizeChange}
      onFontStyleChange={handleFontStyleChange}
      onPointChange={handlePointChange}
      onSizeChange={handleSizeChange}
      onTextChange={setValue}
    />
  );
}

export default RequesterTextFieldRenderer;
